import { useEffect, useState } from "react";
import { Card, Grid } from "@mui/material";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import dayjs from "dayjs";

import { InboundLayout } from "ui-components/Layout";
import { FormTitle, SelectFields, InboundSaveButton } from "ui-components/Form";
import { Success, Error_, LoadingDialog } from "ui-components/Alert";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { headerRender, headerValues, headerValidation } from "./load-manifest-schema";
import LoadManifest from "./components/LoadManifest";
import { manifestColumns } from "./columns";

import { useCustomerValue } from "providers/CustomerContext";

import { createLoadManifest, getFilterOptions, getDOData } from "services/data.outbound.service";
import { StripedCheckboxDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import { useNavigate } from "react-router-dom";

const defaultValues = {
  ...headerValues,
  loadingDatetime: dayjs(new Date()).format("YYYY-MM-DD"),
  transportArrangement: "WAREHOUSE",
  status: "Open",
};

function AddLoadManifest() {
  const { customerCode } = useCustomerValue();
  const navigate = useNavigate();
  const formValues = { ...defaultValues, customerCode };
  const [alert, setAlert] = useState(null);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const [custOrdNoOptions, setCustOrdNoOptions] = useState([]);
  const [shipingZoneOptions, setShippingZoneOptions] = useState([]);
  const [doInvNoOptions, setDoInvNoOptions] = useState([]);

  const [custOrdNbr, setCustOrdNbr] = useState("");
  const [shippingZone, setShippingZone] = useState("");
  const [doNumber, setDONumber] = useState("");

  const [doInvDetails, setDOInvDetails] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // useEffect(() => {
  //   if (customerCode) setFormValues({ ...defaultValues, customerCode });
  // }, [customerCode]);

  useEffect(() => {
    setValue("noOfDroppoint", selectedIds.length);
  }, [selectedIds]);

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });

  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    handleSubmit,
    trigger,
  } = form;

  const headerProps = {
    headerRender,
    customerCode,
    control,
    errors,
  };

  useQuery({
    queryKey: ["lm-filter"],
    queryFn: () => getFilterOptions({ customerCode }),
    enabled: customerCode !== null,
    onSuccess: (data) => {
      const { custOrdNo, shippingZone, doInv } = data.data.data;
      setCustOrdNoOptions(custOrdNo);
      setShippingZoneOptions(shippingZone);
      setDoInvNoOptions(doInv);
    },
  });
  const { isFetching, refetch } = useQuery({
    queryKey: ["do-data"],
    queryFn: () =>
      getDOData({
        customerCode,
        custOrdNbr: custOrdNbr ?? "",
        shippingZone: shippingZone ?? "",
        doNumber: doNumber ?? "",
      }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => setDOInvDetails(data.data.data),
    onError,
  });

  const createLoadManifestMutation = useMutation(createLoadManifest, {});
  const createStatus = createLoadManifestMutation.status === "loading";

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) return;

    const data = getValues();

    const req = {
      ...data,
      selectedIds: selectedIds.map((selected) =>
        doInvDetails.find((detail) => detail.lineNo == selected)
      ),
    };

    createLoadManifestMutation.mutate(req, {
      onSuccess: (data) => {
        navigate("/outbound/load-manifest", { state: data.data.message });
      },
      onError,
    });
  };
  return (
    <InboundLayout lg={12}>
      {(isFetching || createStatus) && <LoadingDialog />}
      {alert}
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <form noValidate>
            <LoadManifest {...headerProps} />
          </form>
          <MDBox sx={{ margin: "1rem 0" }} />
          <FormTitle title="Delivery Order List" />
          <MDBox mt={1.625}>
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <SelectFields
                  options={custOrdNoOptions}
                  field={{ label: "Customer Order No", name: "custOrderNo", errors: {} }}
                  onChange={(e, option) => setCustOrdNbr(option)}
                />
              </Grid>
              <Grid item xs={3}>
                <SelectFields
                  options={doInvNoOptions}
                  field={{ label: "DO Inv Number", name: "doInvNumber", errors: {} }}
                  onChange={(e, option) => setDONumber(option)}
                />
              </Grid>
              <Grid item xs={3}>
                <SelectFields
                  options={shipingZoneOptions}
                  field={{ label: "Shipping Zone", name: "shippingZone", errors: {} }}
                  onChange={(e, option) => setShippingZone(option)}
                />
              </Grid>
              <Grid item xs={3}>
                <MDButton type="submit" variant="gradient" color="info" onClick={refetch}>
                  LOAD
                </MDButton>
              </Grid>
            </Grid>
            <MDTypography mt={1.625} mb={2} color="info" variant="h5">
              Datagrid Display
            </MDTypography>
            <StripedCheckboxDataGrid
              columns={manifestColumns}
              rows={doInvDetails.map((x) => ({ id: x.lineNo, ...x }))}
              onRowSelectionModelChange={(id) => setSelectedIds(id)}
              rowSelectionModel={selectedIds}
            />
          </MDBox>
        </MDBox>
      </Card>
      <InboundSaveButton
        url="/outbound/load-manifest"
        label="Submit"
        onClick={handleSubmit(onSubmit)}
        disabled={selectedIds.length === 0 || isSubmitting}
      />
    </InboundLayout>
  );
}

export default AddLoadManifest;
